import React, { useState } from 'react';
import { entryAPI } from '../services/api';
import FoodCategorySelector from './FoodCategorySelector';
import { handleApiError } from '../utils/errorHandler';

const QuickLogModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    food_categories: [], // Changed to food categories
    bm_consistency: '',
    other_symptoms: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFoodCategoriesChange = (categories) => {
    setFormData(prev => ({
      ...prev,
      food_categories: categories
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await entryAPI.create(formData);
      onClose();
      // Reset form
      setFormData({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        food_categories: [],
        bm_consistency: '',
        other_symptoms: '',
      });
    } catch (error) {
      handleApiError(error, 'Error creating quick log entry');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    // Premium modal overlay with enhanced glassmorphism
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Enhanced backdrop */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-xl"
        onClick={onClose}
      />
      
      {/* Premium modal content */}
      <div className="relative w-full max-w-lg glass-card-strong p-8 scale-in max-h-[90vh] overflow-y-auto premium-scrollbar">
        {/* Header with gradient text */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gradient">Quick Log Entry</h2>
            <p className="text-sm text-neutral-600 mt-1">Fast symptom and meal tracking</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-2xl text-neutral-400 hover:text-neutral-600 hover:bg-glass-white transition-all duration-200 hover:scale-110 group"
          >
            <svg className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Premium Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Date & Time Section */}
          <div className="glass-card p-4 space-y-4">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <span>📅</span>
              <span>Date & Time</span>
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="input-glass"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Time
                </label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  className="input-glass"
                  required
                />
              </div>
            </div>
          </div>

          {/* Food Categories Section */}
          <div className="glass-card p-4 space-y-4">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <span>🍽️</span>
              <span>Food Categories</span>
            </h3>
            <FoodCategorySelector 
              selectedCategories={formData.food_categories}
              onChange={handleFoodCategoriesChange}
            />
          </div>

          {/* Bowel Movement Section */}
          <div className="glass-card p-4 space-y-4">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <span>🚽</span>
              <span>Bowel Movement</span>
            </h3>
            <select
              name="bm_consistency"
              value={formData.bm_consistency}
              onChange={handleInputChange}
              className="select-glass"
            >
              <option value="">Select consistency...</option>
              <option value="solid">🟤 Solid</option>
              <option value="soft">🟫 Soft</option>
              <option value="loose">🟡 Loose</option>
              <option value="liquid">💧 Liquid</option>
            </select>
          </div>

          {/* Symptoms Section */}
          <div className="glass-card p-4 space-y-4">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <span>😷</span>
              <span>Symptoms</span>
            </h3>
            <textarea
              name="other_symptoms"
              value={formData.other_symptoms}
              onChange={handleInputChange}
              rows={3}
              className="textarea-glass"
              placeholder="Describe any symptoms you're experiencing..."
            />
          </div>

          {/* Premium Action Buttons */}
          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1 relative overflow-hidden group"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Save Entry</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickLogModal;